import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import SongScrubber from './SongScrubber';
import api from '../../services/api';
import './ReelsManager.css';

const DURATIONS = [
  { label: '15 seconds', value: 15 },
  { label: '30 seconds', value: 30 },
  { label: '45 seconds', value: 45 },
  { label: '60 seconds', value: 60 },
];

const MIX_RECIPES = [
  { label: 'Clip + song', value: 'clip_song' },
  { label: 'Clip + voice', value: 'clip_voice' },
  { label: 'Clip + song + voice', value: 'clip_song_voice' },
  { label: 'Song + voice', value: 'song_voice' },
];

function cloudinaryThumb(asset) {
  const url = String(asset?.url || '');
  if (asset?.kind === 'video' && url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/so_0,w_400,h_700,c_fill,q_auto,f_jpg/');
  }
  if (url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/w_400,h_700,c_fill,q_auto,f_jpg/');
  }
  return url;
}

function formatElapsed(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}

function jobPercent(job) {
  if (!job) return 0;
  if (job.status === 'ready') return 100;
  if (job.status === 'error') return Math.max(0, Number(job.progress) || 0);
  return Math.max(0, Math.min(99, Number(job.progress) || 0));
}

function jobStage(job) {
  if (!job) return 'Starting…';
  if (job.status === 'ready') return 'Ready';
  if (job.status === 'error') return job.error || 'Failed';
  return job.progress_label || (job.status === 'queued' ? 'Queued…' : 'Working…');
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

function openCloudinary({ resourceType, maxFiles, onFile, onError }) {
  if (!window.cloudinary?.openUploadWidget) {
    onError?.('Cloudinary widget not available');
    return;
  }
  window.cloudinary.openUploadWidget(
    {
      cloudName: 'dtxar5b36',
      uploadPreset: 'bordergarita_uploads',
      sources: ['local'],
      resourceType,
      multiple: true,
      maxFiles: maxFiles || 12,
      clientAllowedFormats:
        resourceType === 'video'
          ? ['mp4', 'mov', 'webm', 'm4v']
          : resourceType === 'raw'
            ? ['mp3', 'wav', 'm4a', 'aac']
            : ['jpg', 'jpeg', 'png', 'webp', 'mp4', 'mov', 'webm', 'mp3', 'wav', 'm4a'],
      folder: 'reel_broll',
    },
    (error, result) => {
      if (error) onError?.(error.message || 'Upload failed');
      if (!error && result.event === 'success') onFile(result.info);
    }
  );
}

export default function ReelsManager() {
  const toast = useRef(null);
  const pollRef = useRef(null);
  const jobToastKeyRef = useRef('');
  const startedAtRef = useRef(0);
  const assetMenuRef = useRef(null);
  const assetMenuTarget = useRef(null);
  const [tab, setTab] = useState('generate');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [voices, setVoices] = useState([]);
  const [hasKey, setHasKey] = useState(true);
  const [ttsProvider, setTtsProvider] = useState('fish');
  const [credits, setCredits] = useState(null);
  const [music, setMusic] = useState({ bundled: [], uploaded: [] });
  const [ruleId, setRuleId] = useState('');
  const [duration, setDuration] = useState(30);
  const [voiceId, setVoiceId] = useState('');
  const [trackId, setTrackId] = useState('none');
  const [script, setScript] = useState('');
  const [caption, setCaption] = useState('');
  const [filling, setFilling] = useState('');
  const [clipId, setClipId] = useState('');
  const [packing, setPacking] = useState(false);
  const [mixing, setMixing] = useState(false);
  const [mixRecipe, setMixRecipe] = useState('clip_song_voice');
  const [songVol, setSongVol] = useState(15);
  const [voiceVol, setVoiceVol] = useState(100);
  const [songStart, setSongStart] = useState(0);
  const [songEnd, setSongEnd] = useState('');
  const [songWindow, setSongWindow] = useState(15);
  const [songDuration, setSongDuration] = useState(0);
  const [mixPreview, setMixPreview] = useState(null);
  const [mixBuiltKey, setMixBuiltKey] = useState('');
  const [songPlayUrl, setSongPlayUrl] = useState('');
  const songBlobRef = useRef('');
  const mixBlobRef = useRef('');
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [cityFilter, setCityFilter] = useState('all');
  const [uploadCity, setUploadCity] = useState('all');

  const load = useCallback(async () => {
    try {
      const [rulesRes, assetsRes, jobsRes, voicesRes, musicRes] = await Promise.all([
        api.get('/social_prepubs'),
        api.get('/reels/assets'),
        api.get('/reels/jobs?limit=20'),
        api.get('/reels/voices'),
        api.get('/reels/music'),
      ]);
      const nextRules = (rulesRes.data || []).filter((r) => r.active !== false);
      setRules(nextRules);
      setAssets(assetsRes.data || []);
      setJobs(jobsRes.data || []);
      setVoices(voicesRes.data?.voices || []);
      setHasKey(Boolean(voicesRes.data?.has_key));
      setTtsProvider(voicesRes.data?.provider || 'none');
      setCredits(voicesRes.data?.credits || null);
      setMusic(musicRes.data || { bundled: [], uploaded: [] });
      setRuleId((prev) => prev || (nextRules[0] && nextRules[0]._id) || '');
      setVoiceId((prev) => prev || voicesRes.data?.default_voice_id || '');
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not load Reel studio',
        detail: err.response?.data?.error || err.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [load]);

  useEffect(() => {
    if (!generating) return undefined;
    const tick = () => setElapsedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [generating]);

  const cities = useMemo(() => {
    const set = new Set(['all']);
    rules.forEach((r) => r.headline_city && set.add(r.headline_city));
    assets.forEach((a) => a.city && set.add(a.city));
    return [...set];
  }, [rules, assets]);

  const broll = useMemo(
    () =>
      assets.filter((a) => {
        if (a.kind === 'audio') return false;
        if (cityFilter === 'all') return true;
        return String(a.city || 'all') === cityFilter;
      }),
    [assets, cityFilter]
  );

  const selectedRule = useMemo(
    () => rules.find((r) => r._id === ruleId) || null,
    [rules, ruleId]
  );

  const clips = useMemo(() => {
    const city = String(selectedRule?.headline_city || '').trim().toLowerCase();
    return assets.filter((a) => {
      if (a.kind === 'audio') return false;
      if (!city) return true;
      const ac = String(a.city || 'all').trim().toLowerCase();
      return ac === 'all' || ac === city || ac.includes(city) || city.includes(ac);
    });
  }, [assets, selectedRule]);

  const musicOptions = useMemo(() => {
    const opts = [{ label: 'No song', value: 'none' }];
    (music.bundled || []).forEach((t) =>
      opts.push({ label: `File · ${t.name}`, value: t.id })
    );
    (music.uploaded || []).forEach((t) =>
      opts.push({ label: `Library · ${t.name}`, value: t.id })
    );
    return opts;
  }, [music]);

  const selectedClip = useMemo(
    () => assets.find((a) => a._id === clipId) || null,
    [assets, clipId]
  );

  const mixVoiceJob = useMemo(() => {
    if (job?.status === 'ready' && job?.output_url) return job;
    return (jobs || []).find((item) => item.status === 'ready' && item.output_url) || null;
  }, [job, jobs]);

  const voiceReady = Boolean(mixVoiceJob?.output_url);
  const hasSong = Boolean(trackId && trackId !== 'none' && trackId !== 'random');

  const mixReady = useMemo(() => {
    if (mixRecipe === 'clip_song') return Boolean(clipId) && hasSong;
    if (mixRecipe === 'clip_voice') return Boolean(clipId) && voiceReady;
    if (mixRecipe === 'clip_song_voice') return Boolean(clipId) && hasSong && voiceReady;
    if (mixRecipe === 'song_voice') return hasSong && voiceReady;
    return false;
  }, [mixRecipe, clipId, hasSong, voiceReady]);

  const mixMissing = useMemo(() => {
    const missing = [];
    if (mixRecipe !== 'song_voice' && !clipId) missing.push('clip');
    if (mixRecipe !== 'clip_voice' && !hasSong) missing.push('song');
    if (mixRecipe !== 'clip_song' && !voiceReady) missing.push('voice MP3');
    return missing;
  }, [mixRecipe, clipId, hasSong, voiceReady]);

  const usesSong = mixRecipe !== 'clip_voice';
  const usesVoice = mixRecipe !== 'clip_song';
  const mixInputsKey = `${mixRecipe}|${clipId}|${trackId}|${mixVoiceJob?._id || ''}|${songVol}|${voiceVol}|${songStart}|${songEnd}|${songWindow}`;
  const mixStale = Boolean(mixPreview && mixBuiltKey !== mixInputsKey);

  useEffect(() => {
    let cancelled = false;
    if (songBlobRef.current) {
      URL.revokeObjectURL(songBlobRef.current);
      songBlobRef.current = '';
    }
    setSongPlayUrl('');
    setSongDuration(0);
    setSongStart(0);
    setSongEnd('');
    setSongWindow(15);
    if (!trackId || trackId === 'none') return undefined;
    api
      .get('/reels/music/file', { params: { id: trackId, inline: 1 }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        songBlobRef.current = url;
        setSongPlayUrl(url);
      })
      .catch(() => {
        if (!cancelled) setSongPlayUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [trackId, music]);

  useEffect(
    () => () => {
      if (songBlobRef.current) URL.revokeObjectURL(songBlobRef.current);
      if (mixBlobRef.current) URL.revokeObjectURL(mixBlobRef.current);
    },
    []
  );

  function stopPoll() {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  async function refreshJob(id) {
    const { data } = await api.get(`/reels/jobs/${id}`);
    setJob(data);
    setJobs((prev) => {
      const rest = (prev || []).filter((item) => item._id !== data._id);
      return [data, ...rest];
    });
    if (data.status === 'ready' || data.status === 'error') {
      stopPoll();
      setGenerating(false);
      const toastKey = `${data._id}:${data.status}`;
      const firstTerminal = jobToastKeyRef.current !== toastKey;
      if (firstTerminal) jobToastKeyRef.current = toastKey;
      const { data: list } = await api.get('/reels/jobs?limit=20');
      setJobs(list || []);
      if (!firstTerminal) return;
      if (data.status === 'ready') {
        toast.current?.show({ severity: 'success', summary: 'Audio ready', life: 2200 });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Reel failed',
          detail: data.error || 'Unknown error',
          life: 6000,
        });
      }
    }
  }

  function startPoll(id) {
    stopPoll();
    jobToastKeyRef.current = '';
    refreshJob(id).catch(() => {});
    pollRef.current = window.setInterval(() => {
      refreshJob(id).catch(() => {});
    }, 800);
  }

  async function fillFromTemplate(mode) {
    if (!ruleId) {
      toast.current?.show({ severity: 'warn', summary: 'Pick a garita template' });
      return;
    }
    if (script.trim() && !window.confirm('Replace the text in the box with a new draft from this template?')) {
      return;
    }
    try {
      setFilling(mode);
      const { data } = await api.post(
        '/reels/script',
        {
          rule_id: ruleId,
          duration_sec: duration,
          mode,
        },
        { timeout: 15000 }
      );
      setScript(data.script || '');
      setCaption(data.caption || '');
      toast.current?.show({
        severity: 'success',
        summary: mode === 'times' ? 'Times loaded' : 'Text ready',
        detail: 'Edit it, then generate the audio.',
        life: 2800,
      });
    } catch (err) {
      setFilling('');
      toast.current?.show({
        severity: 'error',
        summary: 'Could not load template text',
        detail:
          err.code === 'ECONNABORTED'
            ? 'The request timed out. Try again.'
            : err.response?.data?.error || err.message,
        life: 5000,
      });
    } finally {
      setFilling('');
    }
  }

  async function generate() {
    if (!String(script || '').trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'The script box is empty',
        detail: 'Fill it from the template or type your own text.',
      });
      return;
    }
    try {
      startedAtRef.current = Date.now();
      setElapsedSec(0);
      setGenerating(true);
      setJob({
        status: 'queued',
        progress: 1,
        progress_label: 'Queued…',
        rule_name: rules.find((r) => r._id === ruleId)?.name || 'Audio',
      });
      const { data } = await api.post(
        '/reels/generate',
        {
          rule_id: ruleId,
          voice_id: voiceId,
          script,
          caption,
        },
        { timeout: 20000 }
      );
      setJob(data);
      startPoll(data._id);
      toast.current?.show({
        severity: 'info',
        summary: 'Generating MP3',
        detail: 'VALENTINO is reading the text in the box.',
        life: 2800,
      });
    } catch (err) {
      setGenerating(false);
      setJob(null);
      toast.current?.show({
        severity: 'error',
        summary: 'Could not start audio',
        detail:
          err.code === 'ECONNABORTED'
            ? 'The request timed out. Try again.'
            : err.response?.data?.error || err.message,
        life: 5000,
      });
    }
  }

  async function errorDetail(err) {
    const data = err?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        return parsed.error || err.message;
      } catch {
        return err.message;
      }
    }
    return data?.error || err.message;
  }

  function triggerBlob(data, filename, mime) {
    const blob = new Blob([data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function filenameFromResponse(res, fallback) {
    const cd = res.headers?.['content-disposition'] || '';
    const match = String(cd).match(/filename="([^"]+)"/);
    return match ? match[1] : fallback;
  }

  function mixDownloadName(templateName, recipe) {
    const slug =
      String(templateName || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48) || 'garita';
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
    const kind = String(recipe || 'mix').replace(/_/g, '-');
    const ext = recipe === 'song_voice' ? 'm4a' : 'mp4';
    return `${slug}_${stamp}_${kind}.${ext}`;
  }

  async function downloadJob(target) {
    if (!target?._id) return;
    const res = await api.get(`/reels/jobs/${target._id}/download`, { responseType: 'blob' });
    const filename = target.filename || filenameFromResponse(res, 'voice.mp3');
    triggerBlob(res.data, filename, filename.endsWith('.mp3') ? 'audio/mpeg' : 'video/mp4');
  }

  async function downloadClip() {
    if (!clipId) throw new Error('Pick a video clip first');
    const clip = assets.find((a) => a._id === clipId);
    const res = await api.get(`/reels/assets/${clipId}/download`, { responseType: 'blob' });
    const fallback = `${clip?.label || 'clip'}.${clip?.kind === 'image' ? 'jpg' : 'mp4'}`;
    triggerBlob(res.data, filenameFromResponse(res, fallback), clip?.kind === 'image' ? 'image/jpeg' : 'video/mp4');
  }

  async function downloadSong() {
    if (!trackId || trackId === 'none') throw new Error('Pick a song first');
    const res = await api.get('/reels/music/file', { params: { id: trackId }, responseType: 'blob' });
    const named =
      (music.uploaded || []).find((t) => t.id === trackId)?.name ||
      (music.bundled || []).find((t) => t.id === trackId)?.name ||
      'song';
    triggerBlob(res.data, filenameFromResponse(res, `${named}.mp3`), 'audio/mpeg');
  }

  async function downloadWithToast(fn, label) {
    try {
      await fn();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: `${label} failed`,
        detail: await errorDetail(err),
      });
    }
  }

  async function mixAndPreview() {
    if (!mixReady) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Pick the pieces first',
        detail: `Missing: ${mixMissing.join(', ')}`,
      });
      return;
    }
    try {
      setMixing(true);
      const res = await api.post(
        '/reels/mix',
        {
          recipe: mixRecipe,
          clip_id: clipId,
          track_id: trackId,
          job_id: mixVoiceJob?._id || '',
          song_volume: songVol,
          voice_volume: voiceVol,
          song_start_sec: Number(songStart) || 0,
          song_end_sec:
            songEnd === ''
              ? Number(songStart || 0) + Number(songWindow || 15)
              : Number(songEnd) || 0,
          rule_name: selectedRule?.name || selectedRule?.headline_city || '',
        },
        { responseType: 'blob', timeout: 180000 }
      );
      const type = String(res.headers?.['content-type'] || '');
      if (type.includes('application/json')) {
        const parsed = JSON.parse(await res.data.text());
        throw new Error(parsed.error || 'Mix failed');
      }
      const fallback = mixDownloadName(
        selectedRule?.name || selectedRule?.headline_city || 'garita',
        mixRecipe
      );
      const filename = filenameFromResponse(res, fallback);
      const mime = type && !type.includes('octet-stream')
        ? type.split(';')[0].trim()
        : filename.endsWith('.m4a')
          ? 'audio/mp4'
          : 'video/mp4';
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: mime });
      if (mixBlobRef.current) URL.revokeObjectURL(mixBlobRef.current);
      const url = URL.createObjectURL(blob);
      mixBlobRef.current = url;
      setMixPreview({
        url,
        filename,
        mime,
        kind: mime.startsWith('audio') || filename.endsWith('.m4a') ? 'audio' : 'video',
      });
      setMixBuiltKey(mixInputsKey);
      toast.current?.show({
        severity: 'success',
        summary: 'Preview ready',
        detail: 'Play it below. Download when it sounds right.',
        life: 2800,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Mix failed',
        detail: await errorDetail(err),
        life: 6000,
      });
    } finally {
      setMixing(false);
    }
  }

  function downloadMixPreview() {
    if (!mixPreview?.url) return;
    const a = document.createElement('a');
    a.href = mixPreview.url;
    a.download = mixPreview.filename || 'mix.mp4';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function downloadPack() {
    const missing = [];
    if (job?.status !== 'ready') missing.push('MP3');
    if (!clipId) missing.push('clip');
    if (!trackId || trackId === 'none') missing.push('song');
    if (missing.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Need all 3 pieces',
        detail: `Missing: ${missing.join(', ')}`,
      });
      return;
    }
    try {
      setPacking(true);
      await downloadJob(job);
      await new Promise((r) => setTimeout(r, 450));
      await downloadClip();
      await new Promise((r) => setTimeout(r, 450));
      await downloadSong();
      toast.current?.show({
        severity: 'success',
        summary: '3 files downloaded',
        detail: 'Drop the MP3, clip, and song into TikTok / CapCut.',
        life: 3200,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Pack download failed',
        detail: err.response?.data?.error || err.message,
      });
    } finally {
      setPacking(false);
    }
  }

  async function saveAsset(info, kindHint) {
    const payload = {
      url: info.secure_url || info.url,
      public_id: info.public_id,
      resource_type: info.resource_type,
      format: info.format,
      width: info.width,
      height: info.height,
      bytes: info.bytes,
      duration: info.duration,
      city: selectedRule?.headline_city || uploadCity || 'all',
      label: info.original_filename || '',
      kind: kindHint,
    };
    const { data } = await api.post('/reels/assets', payload);
    setAssets((prev) => [data, ...prev]);
  }

  function uploadBroll() {
    openCloudinary({
      resourceType: 'auto',
      onFile: (info) => {
        saveAsset(info).then(() => {
          toast.current?.show({ severity: 'success', summary: 'Clip added', life: 1400 });
        });
      },
      onError: (msg) => toast.current?.show({ severity: 'error', summary: msg }),
    });
  }

  function uploadMusic() {
    openCloudinary({
      resourceType: 'auto',
      maxFiles: 8,
      onFile: (info) => {
        saveAsset(info, 'audio').then(async () => {
          const { data } = await api.get('/reels/music');
          setMusic(data);
          toast.current?.show({ severity: 'success', summary: 'Track added', life: 1400 });
        });
      },
      onError: (msg) => toast.current?.show({ severity: 'error', summary: msg }),
    });
  }

  async function deleteAsset(id, kind = 'clip') {
    const label = kind === 'song' ? 'song' : 'clip';
    if (!window.confirm(`Delete this ${label} from the library and Cloudinary?`)) return;
    try {
      await api.delete(`/reels/assets/${id}`);
      setAssets((prev) => prev.filter((a) => a._id !== id));
      if (clipId === id) setClipId('');
      if (String(trackId) === String(id)) setTrackId('none');
      const { data } = await api.get('/reels/music');
      setMusic(data);
      toast.current?.show({
        severity: 'success',
        summary: `${label[0].toUpperCase()}${label.slice(1)} deleted`,
        life: 1800,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: `Could not delete ${label}`,
        detail: err.response?.data?.error || err.message,
      });
    }
  }

  function openAssetMenu(event, target) {
    event.preventDefault();
    event.stopPropagation();
    assetMenuTarget.current = target;
    assetMenuRef.current?.toggle(event);
  }

  const selectedLibrarySong = (music.uploaded || []).find((t) => t.id === trackId);

  if (loading) {
    return (
      <div className="reel-page">
        <div className="reel-loading">
          <i className="pi pi-spin pi-spinner" />
          <span>Loading Clips Studio…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="reel-page">
      <Toast ref={toast} position="top-center" />
      <Menu
        ref={assetMenuRef}
        popup
        appendTo={typeof document !== 'undefined' ? document.body : null}
        model={[
          {
            label: 'Delete',
            icon: 'pi pi-trash',
            command: () => {
              const target = assetMenuTarget.current;
              if (target?.id) deleteAsset(target.id, target.kind);
            },
          },
        ]}
      />
      <header className="reel-header">
        <p className="reel-kicker">Clips Studio</p>
        <h1>Clips Studio</h1>
        <p className="reel-lead">
          Upload clips and songs, mix them, and download the result.
        </p>
      </header>

      <div className="reel-tabs" role="tablist">
        {['generate', 'history'].map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            className={tab === id ? 'is-active' : ''}
            onClick={() => setTab(id)}
          >
            {id === 'generate' ? 'Generate' : id === 'library' ? 'B-roll' : id === 'music' ? 'Music' : 'History'}
          </button>
        ))}
      </div>

      {tab === 'generate' && (
        <>
          <section className="reel-form">
            {!hasKey && (
              <div className="reel-voice-note">
                No Fish Audio key. Add FISH_AUDIO_API_KEY in the worker .env. Without it the
                audio falls back to a generic TTS voice.
              </div>
            )}
            {hasKey && ttsProvider === 'fish' && (
              <p className="reel-hint">
                Voice: Fish Audio VALENTINO
                {credits
                  ? ` · ${credits.remaining}/${credits.total} credits (${credits.type})`
                  : ''}
              </p>
            )}
            <label>
              Garita template
              <Dropdown
                value={ruleId}
                options={rules.map((r) => ({
                  label: r.name || r.headline_city || r.entry_page_slug,
                  value: r._id,
                }))}
                onChange={(e) => setRuleId(e.value)}
                placeholder="Select FB template"
                className="w-full"
              />
            </label>
            <div className="reel-form-row">
              <label>
                Draft length
                <Dropdown
                  value={duration}
                  options={DURATIONS}
                  onChange={(e) => setDuration(e.value)}
                  className="w-full"
                />
              </label>
              <label>
                Voice
                <Dropdown
                  value={voiceId}
                  options={voices.map((v) => ({
                    label: v.labels ? `${v.name} · ${v.labels}` : v.name,
                    value: v.id,
                  }))}
                  onChange={(e) => setVoiceId(e.value)}
                  className="w-full"
                />
              </label>
            </div>
            <label className="reel-script-label">
              Script
              <textarea
                className="reel-script"
                value={script}
                onChange={(e) => setScript(e.target.value)}
                rows={12}
                placeholder="This box stays open. Fill it from the garita template, or type your own copy, then generate the MP3."
              />
              <span className="reel-hint">
                {script.trim() ? `${script.trim().split(/\s+/).filter(Boolean).length} words` : 'Empty'}
                {caption ? ' · caption saved for later' : ''}
              </span>
            </label>
            <div className="reel-actions reel-actions-3">
              <Button
                label={filling === 'narrative' ? 'Loading…' : 'Fill from template'}
                icon={filling === 'narrative' ? 'pi pi-spin pi-spinner' : 'pi pi-file'}
                className="p-button-outlined"
                disabled={Boolean(filling) || generating || !ruleId}
                onClick={() => fillFromTemplate('narrative')}
              />
              <Button
                label={filling === 'times' ? 'Loading times…' : 'Fill from template + entry times'}
                icon={filling === 'times' ? 'pi pi-spin pi-spinner' : 'pi pi-clock'}
                className="p-button-outlined"
                disabled={Boolean(filling) || generating || !ruleId}
                onClick={() => fillFromTemplate('times')}
              />
              <Button
                label={
                  generating
                    ? `${jobPercent(job)}% · ${jobStage(job)}`
                    : 'Generate audio'
                }
                icon={generating ? 'pi pi-spin pi-spinner' : 'pi pi-volume-up'}
                disabled={generating || Boolean(filling) || !String(script || '').trim()}
                onClick={generate}
              />
            </div>
          </section>

          {job && (
            <section className="reel-preview">
              <div className="reel-preview-head">
                <strong>{job.rule_name || 'Generated audio'}</strong>
                <span className={`reel-status ${job.status}`}>{job.status}</span>
              </div>
              {job.status === 'error' && <p className="reel-hint">{job.error}</p>}
              {job.status !== 'ready' && job.status !== 'error' && (
                <div className="reel-progress">
                  <div className="reel-progress-meta">
                    <strong>{jobPercent(job)}%</strong>
                    <span>{jobStage(job)}</span>
                    <span className="reel-progress-time">{formatElapsed(elapsedSec)}</span>
                  </div>
                  <div className="reel-progress-bar" aria-valuenow={jobPercent(job)} aria-valuemin={0} aria-valuemax={100}>
                    <div className="reel-progress-fill" style={{ width: `${jobPercent(job)}%` }} />
                  </div>
                  {job.status === 'queued' && elapsedSec >= 20 && (
                    <p className="reel-hint">
                      Still queued after {formatElapsed(elapsedSec)}. Is the worker running? Rebuild
                      and restart it with <code>npm run build && node dist/server.js</code>.
                    </p>
                  )}
                </div>
              )}
              {job.status === 'ready' && job.output_url ? (
                <div className="reel-audio">
                  <audio src={job.output_url} controls preload="metadata" />
                  <p className="reel-hint">
                    {job.vo_duration_sec ? `${job.vo_duration_sec}s · ` : ''}
                    {job.filename || 'voice.mp3'}
                  </p>
                  <div className="reel-actions reel-actions-end">
                    <Button
                      label="Download MP3"
                      icon="pi pi-download"
                      onClick={() => downloadWithToast(() => downloadJob(job), 'MP3')}
                    />
                  </div>
                </div>
              ) : job.status === 'error' ? null : (
                <div className="reel-loading" style={{ minHeight: 120 }}>
                  <i className="pi pi-spin pi-spinner" />
                  <strong>{jobPercent(job)}%</strong>
                  <span>{jobStage(job)}</span>
                </div>
              )}
            </section>
          )}

          <section className="reel-card">
            <div className="reel-preview-head">
              <strong>Video clip</strong>
              <Button label="Upload clips" icon="pi pi-upload" className="p-button-outlined p-button-sm" onClick={uploadBroll} />
            </div>
            <p className="reel-hint" style={{ marginBottom: '0.75rem' }}>
              Pick one clip for TikTok. Filtered to {selectedRule?.headline_city || 'this template'} when tagged.
            </p>
            {clips.length === 0 ? (
              <p className="reel-hint">No clips yet. Upload a vertical video of the line.</p>
            ) : (
              <div className="reel-grid">
                {clips.map((asset) => (
                  <div key={asset._id} className={`reel-thumb-wrap ${clipId === asset._id ? 'is-selected' : ''}`}>
                    <button
                      type="button"
                      className={`reel-thumb ${clipId === asset._id ? 'is-selected' : ''}`}
                      onClick={() => setClipId((prev) => (prev === asset._id ? '' : asset._id))}
                    >
                      <img src={cloudinaryThumb(asset)} alt="" />
                      {clipId === asset._id && (
                        <span className="reel-thumb-check" aria-hidden>
                          <i className="pi pi-check" />
                        </span>
                      )}
                      <div className="reel-thumb-meta">
                        {asset.city || 'all'} · {asset.kind}
                      </div>
                    </button>
                    <button
                      type="button"
                      className="reel-thumb-menu"
                      aria-label="Clip options"
                      onClick={(e) => openAssetMenu(e, { id: asset._id, kind: 'clip' })}
                    >
                      <i className="pi pi-ellipsis-v" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {selectedClip?.url && (
              <div className="reel-player">
                {selectedClip.kind === 'video' ? (
                  <video src={selectedClip.url} controls playsInline preload="metadata" />
                ) : (
                  <img src={selectedClip.url} alt={selectedClip.label || 'clip'} />
                )}
                <p className="reel-hint">Selected clip — hit play to preview.</p>
              </div>
            )}
          </section>

          <section className="reel-card">
            <div className="reel-preview-head">
              <strong>Song</strong>
              <Button label="Upload MP3" icon="pi pi-upload" className="p-button-outlined p-button-sm" onClick={uploadMusic} />
            </div>
            <div className="reel-song-row">
              <label>
                Background track
                <Dropdown
                  value={trackId}
                  options={musicOptions}
                  onChange={(e) => setTrackId(e.value)}
                  className="w-full"
                />
              </label>
              {selectedLibrarySong && (
                <button
                  type="button"
                  className="reel-song-menu"
                  aria-label="Song options"
                  onClick={(e) => openAssetMenu(e, { id: selectedLibrarySong.id, kind: 'song' })}
                >
                  <i className="pi pi-ellipsis-v" />
                </button>
              )}
            </div>
            {hasSong && (
              <div className="reel-audio" style={{ marginTop: '0.85rem' }}>
                {songPlayUrl ? (
                  <SongScrubber
                    url={songPlayUrl}
                    start={songStart}
                    windowSec={songWindow}
                    songDuration={songDuration}
                    onDuration={setSongDuration}
                    onChange={(from, to, win) => {
                      setSongStart(from);
                      setSongEnd(to);
                      if (win) setSongWindow(win);
                    }}
                  />
                ) : (
                  <p className="reel-hint">Loading song…</p>
                )}
              </div>
            )}
          </section>

          <section className="reel-card">
            <div className="reel-preview-head">
              <strong>Mix & download</strong>
            </div>
            <p className="reel-hint">
              Mix with only the pieces that combo needs. Clip + song does not need a voice MP3.
            </p>
            <label style={{ marginTop: '0.75rem' }}>
              Combination
              <Dropdown
                value={mixRecipe}
                options={MIX_RECIPES}
                onChange={(e) => setMixRecipe(e.value)}
                className="w-full"
              />
            </label>
            <div className="reel-vols">
              {usesVoice && (
                <label className="reel-vol">
                  <span>Voice {voiceVol}%</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={voiceVol}
                    onChange={(e) => setVoiceVol(Number(e.target.value))}
                  />
                </label>
              )}
              {usesSong && (
                <label className="reel-vol">
                  <span>Song {songVol}%</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={songVol}
                    onChange={(e) => setSongVol(Number(e.target.value))}
                  />
                </label>
              )}
            </div>
            <div className="reel-actions reel-actions-end" style={{ marginTop: '0.85rem' }}>
              <Button
                label={
                  mixing
                    ? 'Mixing… this can take a minute'
                    : mixPreview && !mixStale
                      ? 'Preview again'
                      : 'Preview mix'
                }
                icon={mixing ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
                disabled={mixing || !mixReady}
                onClick={mixAndPreview}
              />
            </div>
            {!mixReady && (
              <p className="reel-hint" style={{ marginTop: '0.55rem' }}>
                Select {mixMissing.join(', ')} to enable this mix.
              </p>
            )}
            {mixStale && (
              <p className="reel-hint" style={{ marginTop: '0.55rem' }}>
                Volume or pieces changed. Preview again to hear the new mix.
              </p>
            )}
            {mixPreview?.url && (
              <div className="reel-mix-preview">
                {mixPreview.kind === 'video' ? (
                  <div className="reel-phone">
                    <video
                      key={mixPreview.url}
                      src={mixPreview.url}
                      controls
                      playsInline
                      autoPlay
                      preload="auto"
                    />
                  </div>
                ) : (
                  <audio key={mixPreview.url} src={mixPreview.url} controls autoPlay preload="auto" />
                )}
                <div className="reel-actions reel-actions-end">
                  <Button
                    label="Download mix"
                    icon="pi pi-download"
                    disabled={mixing}
                    onClick={downloadMixPreview}
                  />
                </div>
                <p className="reel-hint">{mixPreview.filename}</p>
              </div>
            )}
          </section>
        </>
      )}

      {tab === 'library' && (
        <section>
          <div className="reel-toolbar">
            <Dropdown
              value={uploadCity}
              options={cities.map((c) => ({ label: c === 'all' ? 'All cities' : c, value: c }))}
              onChange={(e) => setUploadCity(e.value)}
              editable
            />
            <Dropdown
              value={cityFilter}
              options={cities.map((c) => ({ label: c === 'all' ? 'Show all' : c, value: c }))}
              onChange={(e) => setCityFilter(e.value)}
            />
            <Button label="Upload clips" icon="pi pi-upload" onClick={uploadBroll} />
          </div>
          <p className="reel-hint" style={{ marginBottom: '0.85rem' }}>
            Upload vertical photos or videos of the line. New uploads use the city on the left so random
            picks stay on that garita.
          </p>
          {broll.length === 0 && (
            <div className="reel-empty">
              <p>No clips yet. Upload a handful of Tijuana / Otay / San Ysidro shots and you are done.</p>
            </div>
          )}
          <div className="reel-grid">
            {broll.map((asset) => (
              <article key={asset._id} className="reel-thumb">
                {asset.kind === 'video' ? (
                  <img src={cloudinaryThumb(asset)} alt="" />
                ) : (
                  <img src={cloudinaryThumb(asset)} alt="" />
                )}
                <Button
                  icon="pi pi-ellipsis-v"
                  className="p-button-text p-button-rounded p-button-sm reel-thumb-del"
                  onClick={(e) => openAssetMenu(e, { id: asset._id, kind: 'clip' })}
                />
                <div className="reel-thumb-meta">
                  {asset.city || 'all'} · {asset.kind}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {tab === 'music' && (
        <section className="reel-card">
          <p className="reel-hint">
            Upload royalty-free MP3s here, or drop files into <code>borderGaritaWorker/assets/reel-music/</code>.
            Copyrighted songs can get the reel muted on TikTok/IG.
          </p>
          <div className="reel-toolbar" style={{ marginTop: '0.85rem' }}>
            <Button label="Upload MP3" icon="pi pi-upload" onClick={uploadMusic} />
          </div>
          {(music.bundled || []).map((t) => (
            <div key={t.id} className="reel-job">
              <div>
                <strong>{t.name}</strong>
                <span>Bundled file</span>
              </div>
              <Tag value="file" />
            </div>
          ))}
          {(music.uploaded || []).map((t) => (
            <div key={t.id} className="reel-job">
              <div>
                <strong>{t.name}</strong>
                <span>{t.city || 'all'}</span>
              </div>
              <Button
                icon="pi pi-ellipsis-v"
                className="p-button-text p-button-rounded"
                onClick={(e) => openAssetMenu(e, { id: t.id, kind: 'song' })}
              />
            </div>
          ))}
          {!music.bundled?.length && !music.uploaded?.length && (
            <p className="reel-hint">No tracks yet. Generate still works — a simple bed is mixed under the voice.</p>
          )}
        </section>
      )}

      {tab === 'history' && (
        <section>
          {jobs.length === 0 && (
            <div className="reel-empty">
              <p>No audios yet. Generate one from the first tab.</p>
            </div>
          )}
          {jobs.map((item) => (
            <article key={item._id} className="reel-card reel-job">
              <div>
                <strong>{item.rule_name || item.headline_city || 'Reel'}</strong>
                <span>
                  {item.duration_sec}s · {new Date(item.createdAt).toLocaleString()}
                  {(item.status === 'queued' || item.status === 'running') &&
                    ` · ${jobPercent(item)}%${item.progress_label ? ` · ${item.progress_label}` : ''}`}
                </span>
                <span className={`reel-status ${item.status}`}>{item.status}</span>
              </div>
              <Button
                label="Download"
                icon="pi pi-download"
                className="p-button-outlined p-button-sm"
                disabled={item.status !== 'ready'}
                onClick={() => downloadWithToast(() => downloadJob(item), 'Download')}
              />
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
